import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "container",
    "loading",
    "summary",
    "locationName",
    "editor",
    "fallback",
    "countryGroup",
    "country",
    "stateGroup",
    "state",
    "error",
    "submit",
    "gstContainer",
    "gstOffer",
    "gstToggle",
    "gstDetails",
    "gstEnabled",
    "gstField",
    "gstin"
  ];
  static values = { url: String, autostart: Boolean };

  connect() {
    this.started = false;
    this.syncGstFields();
    if (this.selectionValid()) {
      this.started = true;
      this.containerTarget.classList.remove("d-none");
      this.presentSelection();
      return;
    }

    if (this.autostartValue) this.start();
  }

  start() {
    if (this.started) return;

    this.started = true;
    this.containerTarget.classList.remove("d-none");
    this.loadingTarget.classList.remove("d-none");
    this.summaryTarget.classList.add("d-none");
    this.editorTarget.classList.add("d-none");
    this.hideGstDisclosure();
    this.setSubmitEnabled(false);
    this.detect();
  }

  edit(event) {
    event.preventDefault();
    this.summaryTarget.classList.add("d-none");
    this.editorTarget.classList.remove("d-none");
    this.clearEditorMessages();
    this.countryGroupTarget.classList.remove("d-none");
    this.syncEditor();
    this.syncGstDisclosure();
    this.countryTarget.focus();
  }

  chooseCountry() {
    if (this.countryTarget.value !== "IN") this.stateTarget.value = "";
    this.clearEditorMessages();
    this.countryGroupTarget.classList.remove("d-none");
    this.presentSelection();
  }

  chooseState() {
    this.clearEditorMessages();
    this.presentSelection();
  }

  presentSelection() {
    this.syncEditor();
    if (!this.selectionValid()) {
      this.hideGstDisclosure();
      this.setSubmitEnabled(false);
      return;
    }

    const select = this.countryTarget.value === "IN" ? this.stateTarget : this.countryTarget;
    const option = select.options[select.selectedIndex];
    this.showSummary(option.text);
  }

  validate(event) {
    if (this.selectionValid()) return;

    event.preventDefault();
    this.start();
    if (this.loadingTarget.classList.contains("d-none")) {
      this.showEditor({ countryKnown: this.countryTarget.value === "IN", validationError: true });
      this.focusMissingSelection();
    }
  }

  async detect() {
    try {
      const response = await fetch(this.urlValue, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        cache: "no-store"
      });
      if (!response.ok) throw new Error("location unavailable");

      const payload = await response.json();
      if (payload.country_code) this.countryTarget.value = payload.country_code;

      if (payload.country_code && payload.country_code !== "IN") {
        if (this.countryTarget.value !== payload.country_code) {
          this.showEditor({ notice: "We couldn't detect your location. Select it to continue." });
          return;
        }

        this.stateTarget.value = "";
        this.showSummary(payload.country_name || this.selectedCountryName());
        return;
      }

      if (!payload.detected || !payload.state_code || !payload.state_name) {
        if (payload.country_code === "IN" && this.countryTarget.value === "IN") {
          this.stateTarget.value = "";
          this.showEditor({
            countryKnown: true,
            notice: "We found India, but not your State/UT. Select it to continue."
          });
        } else {
          this.showEditor({ notice: "We couldn't detect your location. Select it to continue." });
        }
        return;
      }

      this.countryTarget.value = "IN";
      this.stateTarget.value = payload.state_code;
      if (this.stateTarget.value !== payload.state_code) {
        this.stateTarget.value = "";
        this.showEditor({
          countryKnown: true,
          notice: "We found India, but not your State/UT. Select it to continue."
        });
        return;
      }

      this.showSummary(payload.state_name);
    } catch (_error) {
      this.showEditor({ notice: "We couldn't detect your location. Select it to continue." });
    }
  }

  showSummary(stateName) {
    this.loadingTarget.classList.add("d-none");
    this.editorTarget.classList.add("d-none");
    this.clearEditorMessages();
    this.clearSelectionInvalid();
    this.locationNameTarget.textContent = stateName;
    this.summaryTarget.classList.remove("d-none");
    this.syncGstDisclosure();
    this.setSubmitEnabled(true);
  }

  showEditor({ countryKnown = false, notice = "", validationError = false } = {}) {
    this.containerTarget.classList.remove("d-none");
    this.loadingTarget.classList.add("d-none");
    this.summaryTarget.classList.add("d-none");
    this.editorTarget.classList.remove("d-none");
    const stateOnly = countryKnown && this.countryTarget.value === "IN";
    this.countryGroupTarget.classList.toggle("d-none", stateOnly);
    this.fallbackTarget.textContent = notice;
    this.fallbackTarget.classList.toggle("d-none", !notice);
    this.errorTarget.textContent = stateOnly ?
      "Select your State/UT to continue." :
      "Select your country to continue.";
    this.errorTarget.classList.toggle("d-none", !validationError);
    this.setSelectionInvalid(validationError);
    this.syncEditor();
    this.hideGstDisclosure();
    this.setSubmitEnabled(this.selectionValid());
  }

  syncEditor() {
    const india = this.countryTarget.value === "IN";
    this.stateGroupTarget.classList.toggle("d-none", !india);
  }

  selectionValid() {
    if (!this.countryTarget.value) return false;
    return this.countryTarget.value !== "IN" || Boolean(this.stateTarget.value);
  }

  selectedCountryName() {
    const option = this.countryTarget.options[this.countryTarget.selectedIndex];
    return option ? option.text : "Outside India";
  }

  clearEditorMessages() {
    this.fallbackTarget.textContent = "";
    this.fallbackTarget.classList.add("d-none");
    this.errorTarget.classList.add("d-none");
    this.clearSelectionInvalid();
  }

  setSelectionInvalid(invalid) {
    this.clearSelectionInvalid();
    if (!invalid) return;

    const target = this.missingSelectionTarget();
    if (target) target.setAttribute("aria-invalid", "true");
  }

  clearSelectionInvalid() {
    this.countryTarget.removeAttribute("aria-invalid");
    this.stateTarget.removeAttribute("aria-invalid");
  }

  focusMissingSelection() {
    const target = this.missingSelectionTarget();
    if (target) target.focus();
  }

  missingSelectionTarget() {
    if (!this.countryTarget.value) return this.countryTarget;
    if (this.countryTarget.value === "IN" && !this.stateTarget.value) return this.stateTarget;
    return null;
  }

  setSubmitEnabled(enabled) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = !enabled;
  }

  addGst(event) {
    event.preventDefault();
    if (!this.gstInvoiceAvailable() || !this.selectionValid() || !this.hasGstEnabledTarget) return;

    this.gstEnabledTarget.value = "1";
    this.syncGstDisclosure();
    if (this.hasGstinTarget) this.gstinTarget.focus();
  }

  removeGst(event) {
    event.preventDefault();
    this.disableGst();
    this.syncGstDisclosure();
    if (this.gstContainerTarget.classList.contains("d-none") && this.hasSubmitTarget) {
      this.submitTarget.focus();
    } else if (this.hasGstToggleTarget) {
      this.gstToggleTarget.focus();
    }
  }

  uppercaseGstin(event) {
    const input = event.currentTarget;
    input.value = input.value.replace(/\s+/g, "").toUpperCase().slice(0, 15);
  }

  syncGstDisclosure() {
    if (!this.hasGstContainerTarget) return;

    const enabled = this.hasGstEnabledTarget && this.gstEnabledTarget.value === "1";
    const canShowGst = this.selectionValid() && (this.gstInvoiceAvailable() || enabled);
    this.gstContainerTarget.classList.toggle("d-none", !canShowGst);
    if (!canShowGst) {
      this.syncGstFields();
      return;
    }

    this.syncGstFields();
    this.gstOfferTarget.classList.toggle("d-none", enabled);
    this.gstDetailsTarget.classList.toggle("d-none", !enabled);
    this.gstToggleTarget.setAttribute("aria-expanded", enabled ? "true" : "false");
  }

  hideGstDisclosure() {
    if (this.hasGstContainerTarget) this.gstContainerTarget.classList.add("d-none");
    this.syncGstFields();
  }

  syncGstFields() {
    if (!this.hasGstEnabledTarget) return;

    const enabled = this.gstEnabledTarget.value === "1" &&
      this.selectionValid() &&
      !this.gstContainerTarget.classList.contains("d-none");
    if (this.hasGstFieldTarget) {
      this.gstFieldTargets.forEach((field) => { field.disabled = !enabled; });
    }
    if (this.hasGstToggleTarget) {
      this.gstToggleTarget.setAttribute("aria-expanded", enabled ? "true" : "false");
    }
  }

  disableGst() {
    if (!this.hasGstEnabledTarget) return;

    this.gstEnabledTarget.value = "0";
    this.syncGstFields();
  }

  gstInvoiceAvailable() {
    return this.hasGstContainerTarget && this.gstContainerTarget.dataset.gstAvailable === "true";
  }
}
